import { Link } from 'react-router-dom';
import { ArrowRight, Phone, AlertCircle } from 'lucide-react';

const ProblemCard = ({ title, description, causes }) => (
  <div className="bg-slate-50 rounded-xl p-6">
    <div className="flex items-start gap-3 mb-4">
      <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0" />
      <h3 className="text-xl font-semibold text-slate-900">{title}</h3>
    </div>
    <p className="text-slate-600 mb-4">{description}</p>
    <div>
      <span className="text-sm font-medium text-slate-700">Possible causes:</span>
      <ul className="mt-2 space-y-1">
        {causes.map((cause) => (
          <li key={cause} className="text-sm text-slate-600 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
            {cause}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const ProblemsPage = () => {
  return (
    <div data-testid="problems-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-slate-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-teal-600 font-medium text-sm uppercase tracking-wider">
              Troubleshooting
            </span>
            <h1 className="text-4xl font-bold text-slate-900 mt-3 mb-6">
              Common Air-Con Problems
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Understanding what might be wrong with your air conditioning can help you explain the symptoms when you contact us. Here are the most common issues we see.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
                data-testid="problems-cta-book"
              >
                Report a Problem
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:01323643333"
                className="inline-flex items-center gap-2 bg-white text-slate-700 px-6 py-3 rounded-lg font-medium border border-slate-200 hover:bg-slate-50 transition-colors"
              >
                <Phone className="w-4 h-4" />
                01323 643333
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Problems Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            <ProblemCard
              title="No Cold Air"
              description="The most common complaint. Can be caused by low refrigerant, compressor issues, or electrical faults."
              causes={['Refrigerant leak', 'Compressor failure', 'Blocked condenser', 'Faulty pressure switch']}
            />
            <ProblemCard
              title="Weak Airflow"
              description="Air comes out cold but with reduced pressure through the vents."
              causes={['Blocked cabin filter', 'Blower motor issues', 'Ductwork damage', 'Evaporator blockage']}
            />
            <ProblemCard
              title="Bad Smells"
              description="Musty or unpleasant odours when the AC is running."
              causes={['Bacterial growth in evaporator', 'Blocked drain tube', 'Cabin filter needs replacing']}
            />
            <ProblemCard
              title="Strange Noises"
              description="Clicking, squealing, or rattling sounds when AC is engaged."
              causes={['Compressor wear', 'Loose drive belt', 'Debris in system', 'Fan motor issues']}
            />
            <ProblemCard
              title="AC Cycles On and Off"
              description="The compressor engages and disengages frequently."
              causes={['Low refrigerant', 'Faulty pressure switch', 'Electrical issues', 'System blockage']}
            />
            <ProblemCard
              title="Water Inside Car"
              description="Water leaking into the footwell or under the dashboard."
              causes={['Blocked drain tube', 'Evaporator housing damage', 'Heater matrix leak']}
            />
          </div>
        </div>
      </section>

      {/* Important Note */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Don't Ignore Air-Con Problems
            </h2>
            <p className="text-slate-600 mb-4">
              Small issues can become expensive repairs if left unchecked. A refrigerant leak, for example, will only get worse over time and can damage your compressor.
            </p>
            <p className="text-slate-600 mb-8">
              If you've noticed any of these symptoms, get in touch for a proper diagnosis. We'll tell you exactly what's wrong and what it will cost to fix.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Got a Problem?</h2>
          <p className="text-slate-300 mb-8">
            Describe your symptoms and we'll help identify the issue.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors"
            data-testid="problems-cta-bottom"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ProblemsPage;
